// src/components/CurrentOperationsSummary.tsx

import React, { useMemo } from 'react';
import { Conversation } from '../lib/data';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, TrendingUp } from 'lucide-react';
import { DateFilter, FilterType } from './DateFilter';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// The response type definition remains as it's needed for props
export interface ApiSummaryResponse {
  summary: string;
  summary_level: string;
  date_range: string;
  start_date: string;
  end_date: string;
}

interface CurrentOperationsSummaryProps {
  conversations: Conversation[];
  stats: {
    urgent: number;
  };
  // New props passed down from the parent dashboard
  apiSummary: ApiSummaryResponse | null;
  isLoading: boolean;
  error: string | null;
  currentFilterType: FilterType;
  onFilterChange: (filterType: FilterType, date: Date | null) => void;
  // Pass the calculated date range for fallback stats
  dateRange: { start: string; end: string; } | null; 
}

export function CurrentOperationsSummary({
  conversations,
  stats,
  apiSummary,
  isLoading,
  error,
  currentFilterType,
  onFilterChange,
  dateRange,
}: CurrentOperationsSummaryProps) {
  // Fallback stats calculation now uses the dateRange prop
  const fallbackStats = useMemo(() => {
    if (!dateRange) {
        return { total: 0, inProgress: 0, solved: 0, negative: 0 };
    }

    const { start, end } = dateRange;
    const startDate = new Date(start);
    // Set end date to the end of the day for accurate comparison
    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);
    
    const filtered = conversations.filter(c => c.startedAt >= startDate && c.startedAt <= endDate);

    return {
      total: filtered.length,
      inProgress: filtered.filter(c => c.status?.toLowerCase() === 'in progress').length,
      solved: filtered.filter(c => c.status?.toLowerCase() === 'solved').length,
      negative: filtered.filter(c => {
        if (!c.sentiment) return false;
        if (Array.isArray(c.sentiment) && c.sentiment.length > 0) {
          const last = c.sentiment[c.sentiment.length - 1];
          return (last || "").toLowerCase().includes("negative");
        }
        return (c.sentiment || "").toLowerCase().includes("negative");
      }).length,
    };
  }, [conversations, dateRange]);

  const renderContent = () => {
    // Logic remains the same, but uses props instead of state
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span className="text-sm text-gray-600">Loading operational summary...</span>
        </div>
      );
    }
    
    if (error) {
       return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">
              <strong>Error:</strong> {error}
            </p>
            <p className="text-xs text-red-600 mt-1">
              Falling back to client-side calculation...
            </p>
          </div>
        );
    }
    
    if (!dateRange) { // Condition changed from !currentFilter.date to !dateRange
       return (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-gray-100 flex items-center justify-center">
            <TrendingUp className="w-6 h-6" />
          </div>
          <p>Select a date filter to view operation summary</p>
          <p className="text-sm mt-2">Choose daily, weekly, or monthly view above</p>
        </div>
       );
    }

    return (
      <div className="space-y-4">
        {/* API Summary Section */}
        {apiSummary && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="prose prose-sm max-w-none prose-ul:list-disc prose-ul:ml-4">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({children}) => <p className="mb-2">{children}</p>
                }}
              >
                {apiSummary.summary}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {/* Fallback Statistics Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            Statistical Overview ({currentFilterType})
          </h4>
          <p className="text-sm text-gray-800 leading-relaxed">
            The customer service team is managing{' '}
            <strong>{fallbackStats.total} total conversations</strong> with{' '}
            <strong>{fallbackStats.inProgress} cases currently in progress</strong> and{' '}
            <strong>{fallbackStats.solved} successfully resolved</strong>. Critical attention is
            required for <strong>{stats.urgent} urgent conversations</strong>. Sentiment analysis
            reveals <strong>{fallbackStats.negative}</strong> conversations displaying{' '}
            <strong>negative customer emotion</strong>, representing{' '}
            <strong>
              {fallbackStats.total > 0
                ? ((fallbackStats.negative / fallbackStats.total) * 100).toFixed(1)
                : 0}%
            </strong>{' '}
            of all interactions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Current Operations Summary</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              AI-powered insights from operational data
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* The onFilterChange handler now comes directly from props */}
        <DateFilter onFilterChange={onFilterChange} />
        {renderContent()}
      </CardContent>
    </Card>
  );
}